import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  deleteField,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';

function QuestionsFactory(firebase) {
  const addComment = async ({
    sessionId,
    questionId,
    user,
    html,
    parentCommentId
  }) => {
    const ref = collection(
      firebase.fsdb,
      'sessions',
      sessionId,
      'questions',
      questionId,
      'comments'
    );
    return addDoc(ref, {
      uid: user.uid,
      name: user.name,
      avatarUrl: user.avatarUrl,
      html,
      sessionId,
      questionId,
      timestamp: serverTimestamp(),
      parentCommentId
    });
  };

  const editComment = async ({ sessionId, questionId, commentId, html }) => {
    const ref = doc(
      firebase.fsdb,
      'sessions',
      sessionId,
      'questions',
      questionId,
      'comments',
      commentId
    );
    return updateDoc(ref, {
      html
    });
  };

  const deleteComment = async ({ sessionId, questionId, commentId }) => {
    const ref = doc(
      firebase.fsdb,
      'sessions',
      sessionId,
      'questions',
      questionId,
      'comments',
      commentId
    );
    return deleteDoc(ref);
  };

  const addQuestion = async ({
    sessionId,
    questionId,
    html,
    order,
    isVisible
  }) => {
    const ref = doc(
      firebase.fsdb,
      'sessions',
      sessionId,
      'questions',
      questionId
    );

    const questionData = {
      html,
      isVisible,
      order,
      questionId,
      sessionId,
      timestamp: serverTimestamp()
    };

    // If creating as visible, set questionBecameVisibleAt
    if (isVisible) {
      questionData.questionBecameVisibleAt = serverTimestamp();
    }

    return setDoc(ref, questionData);
  };

  const editQuestion = async ({ sessionId, questionId, html }) => {
    const ref = doc(
      firebase.fsdb,
      'sessions',
      sessionId,
      'questions',
      questionId
    );
    return updateDoc(ref, {
      html
    });
  };

  const updateQuestionVisibility = async ({
    sessionId,
    questionId,
    isVisible
  }) => {
    const ref = doc(
      firebase.fsdb,
      'sessions',
      sessionId,
      'questions',
      questionId
    );

    const updateData = { isVisible };

    if (isVisible === true) {
      // Setting to visible: Always set questionBecameVisibleAt to now
      updateData.questionBecameVisibleAt = serverTimestamp();
    } else {
      // Setting to invisible: Delete questionBecameVisibleAt
      updateData.questionBecameVisibleAt = deleteField();
    }

    return updateDoc(ref, updateData);
  };

  const subscribeToAllSessionQuestions = ({ sessionId, snapshot }) => {
    const ref = collection(firebase.fsdb, 'sessions', sessionId, 'questions');
    return onSnapshot(query(ref, orderBy('order', 'asc')), snapshot);
  };

  const subscribeToQuestion = ({ sessionId, snapshot, questionId }) => {
    const ref = collection(firebase.fsdb, 'sessions', sessionId, 'questions');
    return onSnapshot(
      query(
        ref,
        where('questionId', '==', questionId),
        where('isVisible', '==', true),
        orderBy('order', 'asc')
      ),
      snapshot
    );
  };

  const subscribeToComments = ({ sessionId, questionId, snapshot }) => {
    const ref = collection(
      firebase.fsdb,
      'sessions',
      sessionId,
      'questions',
      questionId,
      'comments'
    );
    return onSnapshot(
      query(
        ref,
        where('questionId', '==', questionId),
        orderBy('timestamp', 'asc')
      ),
      snapshot
    );
  };

  const subscribeToAllSessionComments = ({ sessionId, snapshot }) => {
    const ref = collectionGroup(firebase.fsdb, 'comments');
    return onSnapshot(
      query(
        ref,
        where('sessionId', '==', sessionId),
        orderBy('timestamp', 'asc')
      ),
      snapshot
    );
  };

  return {
    addComment,
    addQuestion,
    deleteComment,
    editComment,
    editQuestion,
    subscribeToAllSessionComments,
    subscribeToAllSessionQuestions,
    subscribeToComments,
    subscribeToQuestion,
    updateQuestionVisibility
  };
}

export default QuestionsFactory;
